/* eslint-disable react/jsx-no-useless-fragment */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MenuItem, MenuItemBadge } from '../../data/MenuItem.interface';
import SidenavIcon from './SidenavIcon';

type Props = {
  path: string;
  currentPath: string;
  icon: ReactNode;
  title: string;
  menuItems?: MenuItem[];
  external?: boolean;
  isCollapsed?: boolean;
};

const BADGE_TONE_STYLES: Record<NonNullable<MenuItemBadge['tone']>, string> = {
  new: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  beta: 'bg-sky-100 text-sky-700 ring-sky-200',
  legacy: 'bg-gray-100 text-gray-600 ring-gray-200',
};

const Badge: React.FC<{ badge: MenuItemBadge }> = ({ badge }) => {
  const tone = badge.tone ?? 'new';
  return (
    <span
      className={`ml-2 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${BADGE_TONE_STYLES[tone]}`}
    >
      {badge.label}
    </span>
  );
};

const resolveChildPath = (parentPath: string, child: MenuItem): string => {
  if (child.absolutePath) {
    return child.path.startsWith('/') ? child.path : `/${child.path}`;
  }
  return `/${parentPath}/${child.path}`;
};

const SidenavMenuItem: React.FC<Props> = ({
  path,
  currentPath,
  icon,
  title,
  menuItems,
  external,
  isCollapsed = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [topLevelCurrentPath, setTopLevelCurrentPath] = useState<string>();
  const [secondLevelCurrentPath, setSecondLevelCurrentPath] = useState<string>();
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const flyoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFlyout = !!menuItems?.length;

  useEffect(
    () => () => {
      if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current);
    },
    []
  );

  const handleMouseEnter = () => {
    if (isCollapsed && hasFlyout) {
      if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current);
      setFlyoutOpen(true);
    }
  };
  const handleMouseLeave = () => {
    if (isCollapsed && hasFlyout) {
      flyoutTimeoutRef.current = setTimeout(() => setFlyoutOpen(false), 150);
    }
  };

  // Whether this parent is "active" because the user is on one of its
  // children — handles both the relative-path case (/parent/child) and the
  // absolute-path case where children live at sibling top-level routes.
  const childIsActive = (child: MenuItem): boolean => {
    if (child.absolutePath) {
      const normalized = child.path.startsWith('/') ? child.path.slice(1) : child.path;
      return topLevelCurrentPath === normalized;
    }
    return secondLevelCurrentPath === `${path}/${child.path}`;
  };

  const anyChildActive = !!menuItems?.some(childIsActive);

  useEffect(() => {
    const currentPathSplit = currentPath?.split('/');
    const topLevel = currentPathSplit[1] || '';
    const secondLevel = currentPathSplit[2] || '';
    setTopLevelCurrentPath(topLevel);
    setSecondLevelCurrentPath(secondLevel ? `${topLevel}/${secondLevel}` : undefined);
  }, [currentPath]);

  useEffect(() => {
    if (topLevelCurrentPath === path || anyChildActive) {
      setExpanded(true);
    }
  }, [topLevelCurrentPath, path, anyChildActive]);

  const toggleExpanded = () => {
    setExpanded(prev => !prev);
  };

  // Collapsed rail: a centered icon per item. Items with submenus reveal a
  // flyout of their children on hover (matching the main app); leaf items are
  // icon links with the title as a native tooltip.
  if (isCollapsed) {
    const active = topLevelCurrentPath === path || anyChildActive;
    const iconClass = [
      'group flex h-10 w-10 items-center justify-center rounded-md transition ease-in-out duration-150 hover:bg-gray-100 dark:hover:bg-dark-gray-700',
      active || flyoutOpen
        ? 'bg-gray-100 text-primary-600 dark:bg-dark-gray-700 dark:text-dark-gray-50'
        : 'text-gray-700 dark:text-dark-gray-400',
    ].join(' ');
    return (
      <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {hasFlyout ? (
          <button type="button" className={iconClass} title={title} aria-label={title}>
            <SidenavIcon active={active || flyoutOpen} collapsed>
              {icon}
            </SidenavIcon>
          </button>
        ) : (
          <Link to={`/${path}`} className={iconClass} title={title} aria-label={title}>
            <SidenavIcon active={active} collapsed>
              {icon}
            </SidenavIcon>
          </Link>
        )}

        {hasFlyout && flyoutOpen && (
          <div
            className="absolute top-0 left-full z-50 ml-2 min-w-[190px] rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-dark-gray-700 dark:bg-dark-gray-800"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="mb-1 border-b border-gray-100 px-3 py-1.5 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:border-dark-gray-700 dark:text-gray-400">
              {title}
            </div>
            {menuItems?.map((menuItem, i) => {
              const isActive = childIsActive(menuItem);
              return (
                <Link
                  key={i}
                  to={resolveChildPath(path, menuItem)}
                  onClick={() => setFlyoutOpen(false)}
                  className={[
                    'block px-3 py-2 text-[13px] transition-[color,background-color] duration-150',
                    isActive
                      ? 'bg-primary-50 text-primary-600 dark:bg-dark-gray-700 dark:text-dark-gray-50'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-dark-gray-700/50 dark:hover:text-white',
                  ].join(' ')}
                >
                  <span className="truncate">{menuItem.title}</span>
                  {menuItem.badge && <Badge badge={menuItem.badge} />}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {menuItems?.length ? (
        <div className="space-y-1">
          <button
            className={[
              'group w-full flex items-center pl-2 pr-1 py-2 text-sm font-medium rounded-md bg-white dark:bg-dark-gray-800 text-gray-600 dark:text-dark-gray-400 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-dark-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500',
              topLevelCurrentPath === path || anyChildActive
                ? 'bg-gray-100 dark:bg-dark-gray-700 dark:text-dark-gray-50 dark:hover:text-gray-50'
                : '',
            ].join(' ')}
            onClick={toggleExpanded}
          >
            <SidenavIcon active={topLevelCurrentPath === path || anyChildActive}>
              {icon}
            </SidenavIcon>
            {title}
            <svg
              className={[
                'ml-auto h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150',
                expanded ? 'text-gray-400 rotate-90' : 'text-gray-300',
              ].join(' ')}
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
            </svg>
          </button>
          {expanded && (
            <div className="space-y-1">
              {menuItems.map((menuItem, i) => {
                const isActive = childIsActive(menuItem);
                return (
                  <Link
                    key={i}
                    to={resolveChildPath(path, menuItem)}
                    className={[
                      'group w-full flex items-center pl-11 pr-2 py-2 text-sm font-medium text-gray-600 rounded-md hover:text-gray-900 hover:bg-gray-50',
                      isActive
                        ? 'bg-gray-100 text-primary-600 hover:text-primary-600 dark:bg-dark-gray-700 dark:text-dark-gray-50 dark:hover:text-gray-50'
                        : '',
                    ].join(' ')}
                  >
                    <span className="truncate">{menuItem.title}</span>
                    {menuItem.badge && <Badge badge={menuItem.badge} />}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          {external ? (
            <a
              href="/"
              rel="noopener noreferrer"
              target="_blank"
              className="group flex items-center px-2 py-2 text-sm leading-5 font-medium rounded-md text-gray-700 dark:text-dark-gray-400 focus:outline-none focus:bg-gray-100 transition ease-in-out duration-150 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-gray-400 dark:focus:bg-dark-gray-700 dark:hover:bg-dark-gray-700"
            >
              <SidenavIcon active={topLevelCurrentPath === path}>{icon}</SidenavIcon>
              {title}
            </a>
          ) : (
            <Link
              to={`/${path}`}
              className={[
                'group flex items-center px-2 py-2 text-sm leading-5 font-medium rounded-md text-gray-700 dark:text-dark-gray-400 focus:outline-none focus:bg-gray-100 transition ease-in-out duration-150 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-gray-400 dark:focus:bg-dark-gray-700 dark:hover:bg-dark-gray-700',
                topLevelCurrentPath === path
                  ? 'bg-gray-100 text-primary-600 hover:text-primary-600 dark:bg-dark-gray-700 dark:text-dark-gray-50 dark:hover:text-gray-50'
                  : '',
              ].join(' ')}
            >
              <SidenavIcon active={topLevelCurrentPath === path}>{icon}</SidenavIcon>
              {title}
            </Link>
          )}
        </>
      )}
    </>
  );
};

export default SidenavMenuItem;
